import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { HealthLoopDiagram } from "./HealthLoopDiagram";
import { getLocalized, HealthLang } from "./healthTypes";
import {
    healthColStack,
    healthCol,
    healthContentPadX,
    healthHeroDesktopLayout,
    healthHeroGrid,
    healthHeroLoopWrap,
    healthHeroSectionY,
    healthSectionInnerStyle,
    healthSectionOuter,
} from "./healthLayout";
import {
    healthFocusVisible,
    healthHeroSubtitle,
    healthSectionEyebrow,
    healthHeroSurface,
    healthHeroTitle,
    healthHeroTitleAccent,
    healthPrimaryButton,
    healthSecondaryButton,
    healthButtonArrowFa,
    healthButtonLabel,
} from "./healthVisualStyles";
import { scrollToHealthHashAnchor } from "./healthScroll";

interface HealthHeroProps {
    data: DataKeys;
}

function localePath(path: string, lang: HealthLang): string {
    if (path.startsWith("http") || path.startsWith("mailto")) return path;
    if (lang === "Zh" && path.startsWith("/")) return `/tw${path}`;
    if (lang === "Jp" && path.startsWith("/")) return `/jp${path}`;
    return path;
}

function renderHeroTitle(title: string, accent: string) {
    if (!accent || !title.includes(accent)) {
        return title;
    }
    const index = title.indexOf(accent);
    const before = title.slice(0, index);
    const after = title.slice(index + accent.length);
    return (
        <>
            {before}
            <span className={healthHeroTitleAccent}>{accent}</span>
            {after}
        </>
    );
}

export function HealthHero({ data }: HealthHeroProps) {
    const { lang } = useLang();
    const navigate = useNavigate();
    const hLang = lang as HealthLang;
    const eyebrow = getLocalized(data?.eyebrow, hLang);
    const title = getLocalized(data?.title, hLang);
    const titleAccent = getLocalized(data?.titleAccent, hLang);
    const subtitle = getLocalized(data?.subtitle, hLang);
    const primaryHref = localePath(data?.ctaPrimary?.href ?? "/contact-us", hLang);
    const secondaryAnchor = data?.ctaSecondary?.anchor ?? "#lifecycle";
    const secondaryLabel = getLocalized(data?.ctaSecondary?.label, hLang);
    const primaryLabel = getLocalized(data?.ctaPrimary?.label, hLang);

    return (
        <header className={`${healthSectionOuter} ${healthHeroSurface}`}>
            <div className="m-auto w-full" style={healthSectionInnerStyle}>
                <div
                    className={`${healthContentPadX} ${healthHeroSectionY} ${healthHeroDesktopLayout}`}
                >
                    <div className={healthHeroGrid}>
                        <div className={`${healthCol} ${healthColStack}`}>
                            {eyebrow && <p className={healthSectionEyebrow}>{eyebrow}</p>}
                            <h1 className={`${healthHeroTitle} [text-align:left]`}>
                                {renderHeroTitle(title, titleAccent)}
                            </h1>
                            <p className={`${healthHeroSubtitle} [text-align:left]`}>{subtitle}</p>
                            <div className="flex w-full flex-col gap-3 pt-2 lg:flex-row lg:flex-wrap lg:items-center lg:gap-4">
                                <Link
                                    to={primaryHref}
                                    className={`${healthPrimaryButton} ${healthFocusVisible}`}
                                >
                                    {primaryLabel}
                                </Link>
                                <a
                                    href={secondaryAnchor}
                                    onClick={(event) => {
                                        scrollToHealthHashAnchor(
                                            secondaryAnchor,
                                            event,
                                            true,
                                            (to, options) => navigate(to, options)
                                        );
                                    }}
                                    className={`${healthSecondaryButton} ${healthFocusVisible}`}
                                >
                                    <span className={healthButtonLabel}>{secondaryLabel}</span>
                                    <FontAwesomeIcon icon={faArrowRight} className={healthButtonArrowFa} />
                                </a>
                            </div>
                        </div>

                        <div className={`${healthCol} flex justify-center lg:justify-end`}>
                            <div className={`${healthHeroLoopWrap} relative`}>
                                <div className="w-full">
                                    <HealthLoopDiagram
                                        centerLabel={data?.loopCenterLabel}
                                        centerSubLabel={data?.loopCenterSubLabel}
                                        lang={hLang}
                                        variant="hero"
                                        showNodeTitles
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}
